// Static Ad Generation's form (live's `StaticAdGenerationForm`, spec G14-templates PR 8b): the
// inspirations (the brand's library until the ads area brings Meta's, decision 10), the product,
// and the image model and ratio; one ad an inspiration.
import { Group, Stack, Text, Tooltip } from "@mantine/core";
import { Sparkles } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { Product, StaticAdExampleProduct } from "../../../api/generated/model";
import { ImageTemplateQuote, ImageTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { RatioGlyph } from "../../../ui/RatioGlyph";
import {
  exampleImage,
  ImageSlot,
  mainPicture,
  ProductTool,
  productView,
  ReuseSwitch,
  slotId,
  slotSource,
  StartArrow,
  type FormExample,
  type SlotImage,
  type TemplateFormProps,
} from "./kit";
import { CostChip } from "../TemplateCost";

/** Live's `MAX_REFERENCE_IMAGES`: one ad an inspiration, 1 to 5. */
const MAX_INSPIRATIONS = 5;
/** The model live's form starts on (`GPT_IMAGE_MODEL_ID`), where the catalog offers it. */
const FIRST_MODEL = "gpt-image-2.5-sunburst";

/** The product's picture its ads show, live's `getMainProductImage`: its product shot, else its
 * main picture, else its first. */
export function mainImage(product: Product): string | null {
  return mainPicture(product)?.asset_id ?? null;
}

interface Values {
  inspirations: SlotImage[];
  /** Live's example product (its `PRODUCT_SOURCES.EXAMPLE`): data and a picture, no product row. */
  exampleProduct: StaticAdExampleProduct | null;
  model: string | null;
  ratio: string | null;
}

/**
 * The form's values: the card's example's (its three inspirations, its product as data, live's
 * model and ratio), or empty.
 */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "static-ad-generation") return null;
  const product = inputs.product;
  return {
    inspirations: inputs.inspirations.flatMap((each) => exampleImage(example, each) ?? []),
    exampleProduct: "data" in product ? product : null,
    model: inputs.model,
    ratio: inputs.aspect_ratio,
  };
}

/**
 * Live's form: "Reuse example" (on fills the card's example, its product as live's own, given as
 * data; off clears the form; Replicate opens it on), the inspiration tiles, and in the footer the
 * product (the example's, or one the member picks from the brand's, which replaces it), the
 * model and the ratio (live's tooltips), and the round start, off until at least one inspiration
 * and a product with a picture are there.
 */
export function StaticAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [inspirations, setInspirations] = useState<SlotImage[]>(start?.inspirations ?? []);
  const [productId, setProductId] = useState<string | null>(null);
  const [exampleProduct, setExampleProduct] = useState<StaticAdExampleProduct | null>(
    start?.exampleProduct ?? null,
  );
  const [chosenModel, setModel] = useState<string | null>(start?.model ?? null);
  const [chosenRatio, setRatio] = useState<string | null>(start?.ratio ?? null);
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setInspirations(values?.inspirations ?? []);
    setProductId(null);
    setExampleProduct(values?.exampleProduct ?? null);
    setModel(values?.model ?? null);
    setRatio(values?.ratio ?? null);
  };
  const products = useListProducts(teamId, brandId).data ?? [];
  const models = (useGetCatalog().data?.image_models ?? []).filter((each) =>
    each.job_types.includes("image.advanced"),
  );
  const model =
    models.find((each) => each.id === chosenModel) ??
    models.find((each) => each.id === FIRST_MODEL) ??
    models[0];
  const ratio =
    chosenRatio !== null && model?.aspect_ratios.values.includes(chosenRatio) === true
      ? chosenRatio
      : (model?.aspect_ratios.default ?? null);
  const product = products.find((each) => each.id === productId);
  const image = product === undefined ? null : mainImage(product);
  // The brand's product with its picture, or the example's as it is (live's `toAdCopyProductArgs`).
  const chosen =
    product !== undefined && image !== null
      ? { product_id: product.id, image_ids: [image] }
      : (exampleProduct ?? null);
  const inputs =
    inspirations.length === 0 || chosen === null || model === undefined || ratio === null
      ? null
      : {
          template: "static-ad-generation" as const,
          inspirations: inspirations.map(slotSource),
          product: chosen,
          model: model.id,
          aspect_ratio: ratio,
        };
  const exampleView =
    exampleProduct === null
      ? null
      : { name: exampleProduct.data.product_name, url: example?.urls[exampleProduct.image.key] ?? null };
  const body = inputs === null ? null : { type: "image.template" as const, inputs };
  const request = ImageTemplateRequest.safeParse(body);
  const quote = ImageTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">Static Ad Generation</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={4} wrap="nowrap">
            <ProductTool
              teamId={teamId}
              brandId={brandId}
              tip="The product this ad is for. Pick it from your team's product list"
              view={product === undefined ? exampleView : productView(product)}
              chosenId={productId}
              onPick={(picked) => {
                setProductId(picked.productId);
                setExampleProduct(null);
              }}
              onRemove={() => {
                setProductId(null);
                setExampleProduct(null);
              }}
            />
            {model === undefined ? null : (
              <Choice
                variant="footer"
                setting="Model"
                icon={<Sparkles size={16} strokeWidth={1.5} />}
                tip="The generation model powering your image"
                width={150}
                value={model.id}
                options={models.map((each) => ({ value: each.id, label: each.name }))}
                onChange={setModel}
              />
            )}
            {model === undefined || ratio === null ? null : (
              <Choice
                variant="footer"
                setting="Aspect ratio"
                icon={<RatioGlyph ratio={ratio} />}
                tip="Pick your output's dimensions from the ratios supported by the selected model."
                width={100}
                value={ratio}
                options={model.aspect_ratios.values.map((each) => ({ value: each, label: each }))}
                onChange={setRatio}
              />
            )}
          </Group>
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={inspirations.length} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <Group gap={8} aria-label="Inspirations">
          {inspirations.map((picked, at) => (
            <ImageSlot
              key={slotId(picked)}
              teamId={teamId}
              brandId={brandId}
              label={`Inspiration ${at + 1}`}
              title="Pick a reference ad"
              value={picked}
              onChange={() => setInspirations(inspirations.filter((_, index) => index !== at))}
            />
          ))}
          {inspirations.length < MAX_INSPIRATIONS ? (
            <Tooltip
              label="Browse and pick reference ads from your Inspiration Library to guide the creative direction."
              multiline
              maw={280}
            >
              <span>
                <ImageSlot
                  teamId={teamId}
                  brandId={brandId}
                  label="Inspiration"
                  title="Pick a reference ad"
                  value={null}
                  onChange={(next) => {
                    if (next !== null && !inspirations.some((each) => slotId(each) === slotId(next))) {
                      setInspirations([...inspirations, next]);
                    }
                  }}
                  tabs={["library"]}
                />
              </span>
            </Tooltip>
          ) : null}
        </Group>
        {product !== undefined && image === null ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            This product has no picture yet. Add one on its page first.
          </Text>
        ) : null}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
